import { ApiError, type ApiErrorSummary } from '../ApiError/ApiError';

export type BaseServiceUrls =
  | '/roster'
  | '/auth'
  | '/search'
  | '/construct'
  | '/session'
  | '/fulfillment'
  | '/rosteradapter'
  | '/data'
  | '/caliper'
  | '/permissions'
  | '/composite';

export type Version = '/v1' | '/v2' | '/v3';

const BASE_TEST_API_URL = 'http://localhost:3000/api';
const BASE_API_URL = '/api';

const APPLICATION_JSON = 'application/json';

const HTTP_STATUS_NO_CONTENT = 204;

export function isTestEnvironment(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  envCheck = () => import.meta.env.TEST
): boolean {
  try {
    return Boolean(envCheck());
  } catch {
    return false;
  }
}

export function buildUrl(
  endpoint: `/${string}`,
  service: BaseServiceUrls,
  version: Version,
  queryParameters?: Record<string, string> | string[][],
  environmentCheck: () => boolean = isTestEnvironment
) {
  const base = environmentCheck() ? BASE_TEST_API_URL : BASE_API_URL;

  const params = new URLSearchParams(queryParameters).toString();

  return base + service + version + endpoint + (params ? `?${params}` : '');
}

export async function fetcher<TResponse>(
  endpoint: `/${string}`,
  config: RequestInit & {
    service: BaseServiceUrls;
    version: Version;
    queryParameters?: Record<string, string> | string[][];
  }
) {
  const { service, version, queryParameters, headers, ...rest } = config;

  const response = await fetch(
    buildUrl(endpoint, service, version, queryParameters),
    {
      headers: {
        'Content-Type': APPLICATION_JSON,
        Accept: APPLICATION_JSON,
        ...headers,
      },
      ...rest,
    }
  );

  if (response.status >= 400) {
    let errorBody;

    try {
      errorBody = (await response.json()) as unknown;
    } catch (_) {
      // Ignore failing to parse the error body
    }

    const errorSummary: ApiErrorSummary = {
      status: response.status,
      statusText: response.statusText,
      errorBody,
      endpoint,
      version,
      response,
    };

    console.error(errorSummary);
    throw new ApiError(
      `Received status code ${
        response.status
      } when making a request to ${endpoint} \n\n
                        Error body:\n${JSON.stringify(errorBody, null, 2)}`,
      errorSummary
    );
  }

  if (response.status === HTTP_STATUS_NO_CONTENT) {
    return undefined as TResponse;
  }

  if (response.headers.get('Content-Type')?.includes(APPLICATION_JSON)) {
    return (await response.json()) as TResponse;
  }

  const unexpectedContentError: ApiErrorSummary = {
    status: response.status,
    statusText: response.statusText,
    endpoint,
    version,
    response,
  };

  console.error(unexpectedContentError);
  throw new ApiError(
    `Received a response with an unexpected content type while making a request to ${endpoint}`,
    unexpectedContentError
  );
}
